import React from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import {
  compareArrays,
  filterAccountabilityAttrs,
  isStateFilled,
} from "../../../../utils/daily-accountability";
import AccountabilityCard from "../../../cards/profile-accountability";
import SvgIcon from "../../../SvgIcon";
import ConfirmationModal from "../../ConfirmationModal";

const weekDays = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const AddAccountabilityTemplate = ({
  template,
  defaultTemplate,
  userToLoadInformation,
  startingCalendarDay = 0,
  organizationOwner,
  templateId,
  fromEdit,
}) => {
  const { t } = useTranslation(["coaching-portal", "common"]);
  const days = Object.fromEntries(weekDays.map((day) => [day, false]));
  const emptyItem = {
    title: "",
    description: "",
    days,
    editing: true,
  };

  const [adding, setAdding] = React.useState(false);
  const [templateName, setTemplateName] = React.useState(template?.name || "");
  const initialIsDefault =
    (template && template?._id === defaultTemplate) || false;
  const [isDefault, setIsDefault] = React.useState(initialIsDefault);
  const [state, setState] = React.useState(template?.list || [emptyItem]);
  const [idToDelete, setIdToDelete] = React.useState(undefined); //this is to save the id to delete, and also open/close the delete modal

  const addItem = () => {
    setAdding(true);
    const newState = state.map((item) => {
      return { ...item, editing: false };
    });
    setState([...newState, emptyItem]);
  };

  const saveTemplateChanges = () => {
    const baseUrl = template
      ? `/actions/daily-accountability/add-template?templateId=${template._id}`
      : `/actions/daily-accountability/add-template`;
    const url = fromEdit
      ? `${baseUrl}${template ? "&" : "?"}fromEdit=true`
      : baseUrl;
    submit(url, {
      method: "POST",
      body: {
        name: templateName,
        list: state.map((item) => filterAccountabilityAttrs(item)),
        isDefault,
        // if we are in an organization case, we need to send the organization owner id
        // to save the template at the owner level
        userToRequest: organizationOwner?._id || userToLoadInformation?._id,
      },
    });
  };
  const handleName = (e) => {
    setTemplateName(e.target.value);
  };
  const handleEditing = (i) => {
    setState(
      state.map((item, index) => {
        return i === index
          ? { ...item, editing: !item.editing }
          : { ...item, editing: false };
      })
    );
  };

  const removeItem = (id) => {
    setIdToDelete(id); // opening the modal sending the id to delete
  };

  const handleCancelDelete = () => {
    setIdToDelete(undefined);
  };

  const handleConfirmDelete = () => {
    if (template && !adding) {
      const newState = state.filter((_item, i) => i !== idToDelete);
      submit(
        `/actions/daily-accountability/remove-item-template?templateId=${template._id}`,
        {
          method: "POST",
          body: {
            name: template.name,
            list: newState.map((item) => filterAccountabilityAttrs(item)),
            isDefault: template.isDefault,
            // if we are in an organization case, we need to send the organization owner id
            // to save the template at the owner level
            userToRequest: organizationOwner?._id || userToLoadInformation?._id,
          },
        }
      );
      setState(newState);
    } else setState(state.filter((_item, i) => i !== idToDelete));
    setIdToDelete(undefined);
  };

  const changed =
    compareArrays(template?.list || [], state) ||
    template?.name !== templateName ||
    // the API can only set a default, not unset it
    (isDefault && !initialIsDefault);
  const canSubmit = isStateFilled(state);

  return (
    <div className="Modal-Box add-accountability-template-modal">
      {/* biome-ignore lint/a11y/useAnchorContent: click-outside backdrop, labelled via aria-label */}
      <a
        href={`/actions/refresh?modal=&templateId=`}
        className="overlay"
        aria-label={t("common:close")}
      />
      <div className="box">
        <header>
          <h2 className="only-show-mobile">
            {templateId
              ? t("addAccountabilityTemplate.editTitle")
              : t("addAccountabilityTemplate.addTitle")}
          </h2>
          <input
            className="input-template-title mobile-hidden"
            placeholder={t("addAccountabilityTemplate.enterTitlePlaceholder")}
            value={templateName}
            name="name"
            onChange={handleName}
          ></input>
          {/* biome-ignore lint/a11y/useSemanticElements: a real link so Rill navigates; role only picks up button styles */}
          <a
            href={`/actions/refresh?modal=&templateId=`}
            role="button"
            data-button-type="tertiary"
            data-icon-only="true"
          >
            <img src="/img/new-icons/red-cross.svg" alt={t("common:close")} />
          </a>
        </header>
        <div className="body">
          <label className="force-only-show-mobile">
            <p className="p4">{t("addAccountabilityTemplate.templateName")}</p>
            <input
              name="name"
              className="input-template-title"
              placeholder={t("addAccountabilityTemplate.enterTitleHere")}
              value={templateName}
              onChange={handleName}
            />
          </label>
          <ul>
            {state.map((item, i) => (
              <AccountabilityCard
                key={item?._id ?? i}
                state={state}
                id={i}
                setState={setState}
                changeOrder={false}
                editing={item.editing || false}
                handleEditing={handleEditing}
                removeItem={() => removeItem(i)}
                startingCalendarDay={startingCalendarDay}
              />
            ))}
          </ul>
          <ConfirmationModal
            isOpen={idToDelete !== undefined}
            title={t("addAccountabilityTemplate.deleteTitle")}
            contentText={t("addAccountabilityTemplate.deleteConfirm")}
            cancelButtonText={t("common:cancel")}
            cancelButtonAction={handleCancelDelete}
            confirmButtonText={t("common:delete")}
            confirmButtonAction={handleConfirmDelete}
            reduceSize={true}
            icon="delete"
          />
          <div className="add-acc-new-footer-buttons">
            <button
              type="button"
              onClick={canSubmit ? addItem : null}
              className={canSubmit ? "acc-btn add-new" : "acc-btn disabld"}
              data-button-type="outline"
              disabled={canSubmit ? undefined : true}
            >
              {t("common:add")} {t("addAccountabilityTemplate.new")}
            </button>
            <a
              href={`/actions/refresh?modal=&templateId=`}
              className=" only-show-mobile acc-btn add-new"
            >
              {t("common:cancel")}
            </a>
          </div>
        </div>
        <footer>
          {/* biome-ignore lint/a11y/useSemanticElements: styled as footer > div; a native checkbox input would need restyling */}
          <div
            role="checkbox"
            aria-checked={isDefault}
            // the API can't unset a default, so the current default stays locked
            aria-disabled={initialIsDefault || undefined}
            className={initialIsDefault ? "opacity-70" : undefined}
            tabIndex={initialIsDefault ? -1 : 0}
            onClick={
              initialIsDefault ? undefined : () => setIsDefault(!isDefault)
            }
            onKeyDown={(e) => {
              if (!initialIsDefault && (e.key === " " || e.key === "Enter")) {
                e.preventDefault();
                setIsDefault(!isDefault);
              }
            }}
          >
            <SvgIcon
              src={`/img/new-icons/${isDefault ? "" : "un"}checked-square.svg`}
              noFilterOnLight={isDefault}
            />
            <p className="p4">{t("addAccountabilityTemplate.setAsDefault")}</p>
            {initialIsDefault && (
              <p className="p5">
                {t("addAccountabilityTemplate.changeDefaultHint")}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={changed && canSubmit ? saveTemplateChanges : null}
            data-button-type="primary"
            disabled={changed && canSubmit ? undefined : true}
          >
            {t("common:save")}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default AddAccountabilityTemplate;
